import React from 'react';
import { Box, Typography } from '@mui/material';

import { selectFilteredOrders } from 'selectors/ordersSelector';
import { useSelector } from 'react-redux';
import { OrderCard } from './components/OrderCard';
import { selectLiveAircraftData } from '../../selectors/liveAircraftSelectors';
import { AircraftStatusPill } from '../../components/AircraftStatusPill';
import { groupOrdersByLandingZoneAndPad, sortOrdersByPriorityAndDate } from '../../utils/orderUtils';
import { Order } from '@m4a3/api-client';

/**
 * Props interface for the UpcomingOrders component
 */
interface UpcomingOrdersProps {
  onClickShowSuggestedDropModalForOrder: (orderId: string) => void;
  onClickInvalidateDropLocation?: (orderId: string, locationId: string) => void;
  updateLandingZoneForOrder: (orderId: string, landingZoneId: string) => void;
  landingZones: any[];
  pads: any[];
  openSuggestModal?: () => void;
  showPadGrouping?: boolean;
  onOpenRouteConfirmation?: (order: Order) => void;
  onAcceptOrder?: (order: Order) => void;
}

export const UpcomingOrders = (props: UpcomingOrdersProps) => {
  // Fetch orders from Redux store using selectors
  const orders = useSelector(selectFilteredOrders);
  // Fetch live aircraft telemetry data from Redux store
  const aircraftData = useSelector(selectLiveAircraftData);
  const { pads } = props;

  // Sort orders by priority (NEXT tags) and delivery date
  const sortedOrders = sortOrdersByPriorityAndDate(orders);

  // Group orders by landing zone and then by UAV, filtering by valid statuses  
  const landingZones = groupOrdersByLandingZoneAndPad(
    sortedOrders,
    pads,
    ['READY', 'DISPATCHED', 'DELIVERED']
  );

  // Sort orders within each pad by status then storePreparedDate
  const sortOrdersByStatusAndTime = (a: Order, b: Order) => {
    const statusOrder = ['READY', 'DISPATCHED', 'DELIVERED', 'COMPLETE'];
    const aStatusIndex = statusOrder.indexOf(a.status);
    const bStatusIndex = statusOrder.indexOf(b.status);
    
    if (aStatusIndex !== bStatusIndex && aStatusIndex !== -1 && bStatusIndex !== -1) {
      return aStatusIndex - bStatusIndex;
    }
    
    const aTime = a.storePreparedDate ? new Date(a.storePreparedDate).getTime() : 0;
    const bTime = b.storePreparedDate ? new Date(b.storePreparedDate).getTime() : 0;
    return aTime - bTime;
  };

  /**
   * Gets the aircraft status component based on the UAV's telemetry data
   * @param uavId - The ID of the UAV to get status for
   * @returns A status pill component or null if no status data is available
   */
  const getAircraftStatus = (uavId?: string | null) => {
    // Return null for invalid UAV IDs or unassigned UAVs
    if (!uavId || uavId === 'unassigned') return null;
    
    // Convert UAV ID to telemetry ID format by removing 'MNA-' prefix
    const telemetryId = uavId.replace('MNA-', '');
    // Get telemetry data for this UAV
    const data = aircraftData[telemetryId];
    // If no telemetry data, return null
    if (!data) return null;

    // Return status pill component
    return (
      <Box component="span" sx={{ 
        ml: 1, 
        display: 'inline-flex',
        alignItems: 'center',
        whiteSpace: 'nowrap'
      }}>
        <AircraftStatusPill status={data.uavState} />
      </Box>
    );
  };

  /**
   * Render the component with a three-level hierarchy:
   * 1. Landing Zones (top level)
   * 2. UAVs within each Landing Zone (indented)
   * 3. Orders within each UAV (further indented)
   */
  return (
    <Box display={'flex'} flexDirection="column" sx={{}}>
      {/* Show "Waiting to fly" message when there are no orders */}
      {orders.length ? null : (
        <Box display="flex" py={2}>
          <h3 className={'text-lg font-semibold text-gray-800'}>Waiting to fly</h3>
        </Box>
      )}
      
      {/* Main container for the hierarchical view */}
      <Box display="flex" flexDirection={'column'}>
        {/* Map through each landing zone */}
        {landingZones.map((landingZone) => (
          <Box
            key={`landing-zone-${landingZone.id}`}
            display={'flex'}
            flexDirection="column"
            pr={4}
            mb={3}>
            {/* Landing Zone Header - Top level */}
            <Box display="flex" py={2}>
              <h3 className={'text-lg font-semibold text-gray-800'}>
                {landingZone.name}
              </h3>
            </Box>
            
            {/* Container for all Pads within this Landing Zone */}
            <Box display={'flex'} flexDirection="column">
              {/* Sort pads alphabetically */}
              {[...landingZone.pads]
                .sort((a, b) => {
                  if (!a.id) return 1; // No Pad goes to end
                  if (!b.id) return -1;
                  return (a.name || '').localeCompare(b.name || '');
                })
                .map((pad) => {
                  const sortedOrders = [...pad.orders].sort(sortOrdersByStatusAndTime);
                  
                  return props.showPadGrouping ? (
                    // With pad grouping - gray box around cards (including "No Pad")
                    <Box 
                      key={`pad-${landingZone.id}-${pad.id}`} 
                      sx={{ 
                        backgroundColor: '#EAECF0',
                        borderRadius: 2,
                        padding: '12px',
                        mb: 2
                      }}
                    >
                      {/* Pad Name Header with aircraft info */}
                      <Box display="flex" alignItems="center" mb={'12px'}>
                        <Typography variant="h6" style={{ fontSize: 14, fontWeight: 600, color: '#14142B' }}>
                          {pad.name} - {pad.aircraft}
                        </Typography>
                        {getAircraftStatus(pad.aircraft !== 'Assign Aircraft' ? pad.aircraft : null)}
                      </Box>
                      
                      {/* Orders in this pad */}
                      <Box display={'flex'} flexDirection="column" gap={'12px'}>
                        {sortedOrders.map(order => (
                          <OrderCard 
                            key={`${order.id}-card`}
                            order={order} 
                            aircraftData={aircraftData}
                            landingZones={props.landingZones}
                            pads={pads}
                            openSuggestModal={props.openSuggestModal}
                            onClickShowSuggestedDropModalForOrder={props.onClickShowSuggestedDropModalForOrder}
                            onClickInvalidateDropLocation={props.onClickInvalidateDropLocation}
                            onOpenRouteConfirmation={props.onOpenRouteConfirmation}
                            updateLandingZoneForOrder={props.updateLandingZoneForOrder}
                            onAcceptOrder={props.onAcceptOrder}
                          />
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    // Without pad grouping - original UAV header style
                    <Box 
                      key={`${landingZone.id}-${pad.id}`} 
                      display={'flex'} 
                      flexDirection="column"
                      ml={1}
                      mb={2}
                    >
                      {/* UAV Header */}
                      <Box display="flex" py={1} alignItems="center">
                        <h4 className={'text-md font-medium text-gray-700'}>
                          <span>{pad.name} - {pad.aircraft}</span> ({pad.orders.length})
                          {getAircraftStatus(pad.aircraft !== 'Assign Aircraft' ? pad.aircraft : null)}
                        </h4>
                      </Box>
                      
                      {/* Orders */}
                      <Box display={'flex'} flexDirection="column" ml={1}>
                        {sortedOrders.map(order => (
                          <Box key={`${order.id}-card`} display="flex" mb={1}>
                            <OrderCard 
                              order={order} 
                              aircraftData={aircraftData}
                              landingZones={props.landingZones}
                              pads={pads}
                              openSuggestModal={props.openSuggestModal}
                              onClickShowSuggestedDropModalForOrder={props.onClickShowSuggestedDropModalForOrder}
                              onClickInvalidateDropLocation={props.onClickInvalidateDropLocation}
                              onOpenRouteConfirmation={props.onOpenRouteConfirmation}
                              updateLandingZoneForOrder={props.updateLandingZoneForOrder}
                              onAcceptOrder={props.onAcceptOrder}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};